import { gql, useApolloClient } from '@apollo/client';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { Server } from '../../apollo/types';
import { useTrackEvent } from '../../segment/segment';
import { ClientInfoEntryPoint, EventTypeEnum } from '../../segment/types';
import { roomAtom } from '../../video-sdk';

type ClientInfoTrackingFlagsQueryData = {
  patientChart?: {
    id: string;
    shortId: string;
    hasCompletedMeasureResponses?: boolean;
    formResponses?: Array<{ id: string; highAcuity: boolean } | null> | null;
  } | null;
};

const GET_CLIENT_INFO_TRACKING_FLAGS = gql`
  query GetClientInfoTrackingFlags($patientShortId: ID!) {
    patientChart(values: { patientShortId: $patientShortId }) {
      id
      shortId
      hasCompletedMeasureResponses
      formResponses {
        id
        highAcuity
      }
    }
  }
`;

export function useTrackClientInfoClicked(patientShortId?: string) {
  const apolloClient = useApolloClient();
  const { userClicked } = useTrackEvent();
  const inSession = !!useAtomValue(roomAtom);

  return useCallback(
    async (entryPoint: ClientInfoEntryPoint) => {
      if (!patientShortId) return;

      const baseProperties = { patientShortId, entryPoint, inSession };
      try {
        const { data } = await apolloClient.query<ClientInfoTrackingFlagsQueryData>({
          query: GET_CLIENT_INFO_TRACKING_FLAGS,
          variables: { patientShortId },
          context: { server: Server.MONOLITH },
        });
        const patientChart = data.patientChart;
        if (!patientChart) {
          userClicked(EventTypeEnum.CLIENT_INFO, baseProperties);
          return;
        }

        userClicked(EventTypeEnum.CLIENT_INFO, {
          ...baseProperties,
          hasCompletedMeasureResponses: patientChart.hasCompletedMeasureResponses ?? false,
          hasHighAcuityFlag: !!patientChart.formResponses?.some((response) => response?.highAcuity),
        });
      } catch {
        userClicked(EventTypeEnum.CLIENT_INFO, baseProperties);
      }
    },
    [apolloClient, inSession, patientShortId, userClicked],
  );
}
